import TextField from '@mui/material/TextField';
import React, { useEffect, useState, useCallback } from 'react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormHelperText from '@mui/material/FormHelperText';
import jsPDF from 'jspdf';
import { useParams, useNavigate } from 'react-router-dom';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import dayjs from 'dayjs';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import UserPackage from '../../models/user_package';
import Orders from '../../collections/orders';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AddBonusDialog from './AddBonusDialog';
import Menus from '../../collections/menus';
import { excuseApi } from '../../commons/http';
import {
  financialStatusTranslations,
  userPackageStatusTranslations,
  sendingInvoiceStatusTranslations,
  getFinancialStatusColor,
  getUserPackageStatusColor,
  getSendingInvoiceStatusColor
} from '../../utils/statusTranslations';
import {
  normal,
  italics,
  bold,
  bolditalics,
} from 'roboto-base64';
import Invoices from '../../collections/invoices';
import { invoiceStatusTranslations } from '../../utils/invoiceTranslations';
import { jwtDecode } from 'jwt-decode';

const UserPackageDetail = (props) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [userPackage, setUserPackage] = useState(null);
  const [packageInvoices, setPackageInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  // Marking a package paid by hand is admin-only — normally it is derived from
  // the invoice (the backend rejects a staff-driven change to PAID with 403).
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        setIsAdmin(jwtDecode(token).role === 'ADMIN');
      } catch (e) { /* malformed token: stay non-admin */ }
    }
  }, []);

  // S21: a package can have SEVERAL invoices (the original plus follow-ups for
  // instant items delivered later), so load them all — not just the latest.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const list = await new Invoices().fetch(1, 50, { user_package_id: id });
        const found = (list || []).filter((inv) => inv.status !== 'CANCELLED');
        if (active) setPackageInvoices(found);
      } catch (e) { /* no invoice yet */ }
    })();
    return () => { active = false; };
  }, [id]);
  const [editMode, setEditMode] = useState(false);
  const [orders, setOrders] = useState([]);
  const [formData, setFormData] = useState({});
  const [originalData, setOriginalData] = useState({});
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [bonusDialogOpen, setBonusDialogOpen] = useState(false);
  const [bonuses, setBonuses] = useState([]);
  const [bonusTotal, setBonusTotal] = useState(0);
  const [menus, setMenus] = useState([]);
  const [loadingMenus, setLoadingMenus] = useState(true);

  useEffect(() => {
    props.setPageTitle('Chi tiết gói dịch vụ');
    fetchUserPackage();
    // Only fetch orders separately if they aren't included in the user package response
    if (!userPackage || !userPackage.orders || userPackage.orders.length === 0) {
      fetchOrders();
    }
    fetchMenus();
  }, [id]);

  const fetchOrders = async () => {
    // Skip if we already have orders from the user package response
    if (userPackage && userPackage.orders && userPackage.orders.length > 0) {
      setOrders(userPackage.orders);
      setLoadingOrders(false);
      return;
    }
    
    try {
      const ordersList = new Orders();
      const success = await ordersList.fetchByUserPackage(id);
      if (success) {
        setOrders(ordersList.items);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoadingOrders(false);
    }
  };

  const fetchMenus = async () => {
    try {
      const menusCollection = new Menus();
      await menusCollection.fetch({ user_package_id: id, per_page: 50 });
      setMenus(menusCollection.items || []);
    } catch (error) {
      console.error('Error fetching menus:', error);
    } finally {
      setLoadingMenus(false);
    }
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.addFileToVFS('Roboto-Normal.ttf', atob(normal));
    doc.addFileToVFS('Roboto-Italics.ttf', atob(italics));
    doc.addFileToVFS('Roboto-Bold.ttf', atob(bold));
    doc.addFileToVFS('Roboto-BoldItalics.ttf', atob(bolditalics));
    doc.addFont('Roboto-Normal.ttf', 'Roboto', 'normal');
    doc.addFont('Roboto-Italics.ttf', 'Roboto', 'italics');
    doc.addFont('Roboto-Bold.ttf', 'Roboto', 'bold');
    doc.addFont('Roboto-BoldItalics.ttf', 'Roboto', 'bolditalics');
    doc.setFont('Roboto');

    // Header with background
    doc.setFillColor(51, 122, 183); // Blue header background
    doc.rect(0, 0, 220, 30, 'F');

    // Title
    doc.setTextColor(255, 255, 255); // White text
    doc.setFontSize(20);
    doc.setFont('Roboto', 'bold');
    doc.text('BÁO CÁO CHI TIẾT GÓI DỊCH VỤ', 105, 20, { align: 'center' });

    // Reset text color for content
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(11);

    // Add company logo or watermark if needed
    // doc.addImage(logoBase64, 'PNG', 10, 10, 30, 30);

    // Info section with subtle background
    doc.setFillColor(240, 240, 240);
    doc.rect(10, 35, 190, 175, 'F');  // Increased height for bonus rows

    // Labels column with bold font
    doc.setFont('Roboto', 'bold');
    const startY = 45;
    const labelX = 15;
    const valueX = 75;
    const lineHeight = 10;

    doc.text('Mã gói dịch vụ:', labelX, startY);
    doc.text('Khách hàng:', labelX, startY + lineHeight);
    doc.text('Địa chỉ khách hàng:', labelX, startY + lineHeight * 2);
    doc.text('Gói dịch vụ:', labelX, startY + lineHeight * 3);
    doc.text('Khối lượng gói:', labelX, startY + lineHeight * 4);
    doc.text('Khối lượng tặng thêm:', labelX, startY + lineHeight * 5);
    doc.text('Tổng khả dụng:', labelX, startY + lineHeight * 6);
    doc.text('Ngày bắt đầu:', labelX, startY + lineHeight * 7);
    doc.text('Ngày kết thúc:', labelX, startY + lineHeight * 8);
    doc.text('Số lượng đơn hàng:', labelX, startY + lineHeight * 9);
    doc.text('Số kg mua gói tháng:', labelX, startY + lineHeight * 10);
    doc.text('Số kg mua lẻ:', labelX, startY + lineHeight * 11);
    doc.text('Thành tiền mua lẻ:', labelX, startY + lineHeight * 12);
    doc.text('Còn lại:', labelX, startY + lineHeight * 13);
    doc.text('Trạng thái:', labelX, startY + lineHeight * 14);
    doc.text('Trạng thái thanh toán:', labelX, startY + lineHeight * 15);
    doc.text('Ghi chú:', labelX, startY + lineHeight * 16);

    // Values with normal font
    doc.setFont('Roboto', 'normal');
    doc.text(`${userPackage.id}`, valueX, startY);
    doc.text(`${userPackage.user?.name || '-'}`, valueX, startY + lineHeight);
    doc.text(`${userPackage.user?.address || '-'}`, valueX, startY + lineHeight * 2);
    doc.text(`${userPackage.package?.name || '-'}`, valueX, startY + lineHeight * 3);

    // Package amount and bonus
    const packageAmount = userPackage.package?.amount || 0;
    doc.text(`${packageAmount} kg`, valueX, startY + lineHeight * 4);
    doc.text(`+${bonusTotal.toFixed(2)} kg`, valueX, startY + lineHeight * 5);
    const totalAvailable = packageAmount + bonusTotal;
    doc.text(`${totalAvailable.toFixed(2)} kg`, valueX, startY + lineHeight * 6);

    doc.text(`${dayjs(userPackage.beginning_date).format('DD/MM/YYYY')}`, valueX, startY + lineHeight * 7);
    doc.text(`${userPackage.ending_date ? dayjs(userPackage.ending_date).format('DD/MM/YYYY') : '-'}`, valueX, startY + lineHeight * 8);
    doc.text(`${orders.filter(order => !['CANCELLED', 'CONFIRMING'].includes(order.status)).length}`, valueX, startY + lineHeight * 9);

    // Monthly package kg - only count items with convert_rate (truthy)
    const monthlyKg = orders
    .filter(order => !['CANCELLED', 'CONFIRMING'].includes(order.status))
    .reduce((total, order) =>
      total + order.order_line_items
        .filter(item => item.order_type === 'MONTHLY_PACKAGE_ORDER')
        .filter(item => item.convert_rate)
        .reduce((sum, item) =>
          sum + parseFloat((item.quantity / item.convert_rate).toFixed(2)), 0
        ), 0
    );
    doc.text(`${monthlyKg.toFixed(2)}`, valueX, startY + lineHeight * 10);

    // Instant order - use raw quantity (no conversion)
    const instantKg = orders
    .filter(order => !['CANCELLED', 'CONFIRMING'].includes(order.status))
    .reduce((total, order) =>
      total + order.order_line_items
        .filter(item => item.order_type === 'INSTANT_ORDER')
        .reduce((sum, item) =>
          sum + item.quantity, 0
        ), 0
    );
    doc.text(`${instantKg.toFixed(2)}`, valueX, startY + lineHeight * 11);

    // Instant order total price
    const instantOrderPrice = orders
      .filter(order => !['CANCELLED', 'CONFIRMING'].includes(order.status))
      .reduce((total, order) =>
        total + order.order_line_items
          .filter(item => item.quantity > 0)
          .filter(item => item.order_type === 'INSTANT_ORDER')
          .reduce((sum, item) =>
            sum + (item.quantity * (item.price || 0)), 0
          ), 0);
    doc.text(`${instantOrderPrice.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })}`, valueX, startY + lineHeight * 12);

    // Remaining quantity = Total available - Monthly used
    const remainingKg = totalAvailable - monthlyKg;
    doc.text(`${remainingKg.toFixed(2)} kg`, valueX, startY + lineHeight * 13);

    // Status indicators with colored circles
    const drawStatusCircle = (x, y, color) => {
      doc.setFillColor(color);
      doc.circle(x - 4, y - 3, 2, 'F');
    };

    // Package status with color indicator
    drawStatusCircle(valueX, startY + lineHeight * 14, getUserPackageStatusColor(userPackage.status) === 'success' ? '#4caf50' : '#ff9800');
    doc.text(`${userPackageStatusTranslations[userPackage.status]}`, valueX, startY + lineHeight * 14);

    // Financial status with color indicator
    drawStatusCircle(valueX, startY + lineHeight * 15, getFinancialStatusColor(userPackage.financial_status) === 'success' ? '#4caf50' : '#ff9800');
    doc.text(`${financialStatusTranslations[userPackage.financial_status] || userPackage.financial_status}`, valueX, startY + lineHeight * 15);

    doc.text(`${userPackage.note || '-'}`, valueX, startY + lineHeight * 16);

    // Orders section header with background
    let yOffset = 225;  // Adjusted for additional bonus rows
    doc.setFillColor(51, 122, 183, 0.1); // Light blue background
    doc.rect(10, yOffset - 5, 190, 15, 'F');

    doc.setFont('Roboto', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(255, 255, 255); // Set text color to white
    doc.text('DANH SÁCH ĐƠN HÀNG', 105, yOffset + 5, { align: 'center' });
    doc.setTextColor(0, 0, 0); // Reset text color back to black for subsequent text
    yOffset += 20;

    const pageHeight = doc.internal.pageSize.height;
    const pageWidth = doc.internal.pageSize.width;
    const margin = 10;
    const usableWidth = pageWidth - (2 * margin);

    // Orders table headers
    doc.setFontSize(10);
    doc.setFont('Roboto', 'normal');
    doc.setLineWidth(0.2);

    // Define column widths for orders table
    const orderCols = [
      { width: usableWidth * 0.10, title: 'Mã ĐH' },
      { width: usableWidth * 0.30, title: 'Tên đơn hàng' },
      { width: usableWidth * 0.20, title: 'Loại đơn hàng' },
      { width: usableWidth * 0.20, title: 'Trạng thái' },
      { width: usableWidth * 0.20, title: 'Ngày xử lý' }
    ];

    // Draw orders table header
    let currentX = margin;
    orderCols.forEach(col => {
      doc.rect(currentX, yOffset, col.width, 10);
      doc.text(col.title, currentX + 2, yOffset + 7);
      currentX += col.width;
    });
    yOffset += 10;

    // Draw orders data - filter out cancelled orders
    orders.filter(order => !['CANCELLED', 'CONFIRMING'].includes(order.status)).forEach((order) => {
      if (yOffset > pageHeight - 20) {
        doc.addPage();
        yOffset = margin;
      }

      currentX = margin;
      const rowHeight = 10;

      // Order row
      doc.rect(currentX, yOffset, orderCols[0].width, rowHeight);
      doc.text(order.id.toString(), currentX + 2, yOffset + 7);
      currentX += orderCols[0].width;

      doc.rect(currentX, yOffset, orderCols[1].width, rowHeight);
      doc.text(order.name, currentX + 2, yOffset + 7);
      currentX += orderCols[1].width;

      doc.rect(currentX, yOffset, orderCols[2].width, rowHeight);
      doc.text(`${order.order_type === 'INSTANT_ORDER' ? 'Mua lẻ' : order.order_type === 'MONTHLY_PACKAGE_ORDER' ? 'Gói tháng' : order.order_type || '-'}`, currentX + 2, yOffset + 7);
      currentX += orderCols[2].width;

      doc.rect(currentX, yOffset, orderCols[2].width, rowHeight);
      const statusText = order.status === 'OPEN' ? 'Mới' :
        order.status === 'CANCELLED' ? 'Đã hủy' :
          order.status === 'SENT_INVOICE' ? 'Đã gửi hóa đơn' :
            order.status;
      doc.text(statusText, currentX + 2, yOffset + 7);
      currentX += orderCols[2].width;

      doc.rect(currentX, yOffset, orderCols[3].width, rowHeight);
      doc.text(order.processed_at ? dayjs(order.processed_at).format('DD/MM/YYYY') : '-', currentX + 2, yOffset + 7);

      yOffset += rowHeight + 5;

      // Line items table if exists
      if (order.order_line_items && order.order_line_items.filter(item => item.quantity > 0).filter(item => item.quantity > 0).length > 0) {
        // Check if we need a new page for the line items table
        if (yOffset > pageHeight - 20) { // Need more space for the table header
          doc.addPage();
          yOffset = margin;
        }
        
        // Line items table headers
        const lineItemCols = [
          { width: usableWidth * 0.25, title: 'Sản phẩm' },
          { width: usableWidth * 0.15, title: 'SL (Thực nhận)' },
          { width: usableWidth * 0.15, title: 'SL (Quy đổi)' },
          { width: usableWidth * 0.10, title: 'Loại' },
          { width: usableWidth * 0.10, title: 'Đơn giá' },
          { width: usableWidth * 0.10, title: 'Thành tiền' },
          { width: usableWidth * 0.15, title: 'Ghi chú' }
        ];

        currentX = margin;

        // Draw line items header
        lineItemCols.forEach(col => {
          doc.rect(currentX, yOffset, col.width, 8);
          doc.text(col.title, currentX + 2, yOffset + 6);
          currentX += col.width;
        });
        yOffset += 8;

        // Draw line items data
        order.order_line_items.filter(item => item.quantity > 0).forEach((item) => {
          // Check if we need a new page, including space for the row and potential totals
          if (yOffset > pageHeight - 20) {
            doc.addPage();
            yOffset = margin;
          }

          currentX = margin;
          const itemRowHeight = 8;

          doc.rect(currentX, yOffset, lineItemCols[0].width, itemRowHeight);
          doc.text(item.product?.name || 'N/A', currentX + 2, yOffset + 6);
          currentX += lineItemCols[0].width;

          doc.rect(currentX, yOffset, lineItemCols[1].width, itemRowHeight);
          doc.text(item.quantity.toString(), currentX + 2, yOffset + 6);
          currentX += lineItemCols[1].width;

          doc.rect(currentX, yOffset, lineItemCols[2].width, itemRowHeight);
          doc.text(item.convert_rate ? (item.quantity / item.convert_rate).toFixed(2) : '-', currentX + 2, yOffset + 6);
          currentX += lineItemCols[2].width;

          doc.rect(currentX, yOffset, lineItemCols[3].width, itemRowHeight);
          doc.text(`${item.order_type === 'INSTANT_ORDER' ? 'Mua lẻ' : item.order_type === 'MONTHLY_PACKAGE_ORDER' ? 'Gói tháng' : item.order_type || '-'}`, currentX + 2, yOffset + 6);
          currentX += lineItemCols[3].width;

          // Add price column for instant orders
          doc.rect(currentX, yOffset, lineItemCols[4].width, itemRowHeight);
          doc.text(item.order_type === 'INSTANT_ORDER' ? 
            item.price?.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' }) || '-' : 
            '-', 
            currentX + 2, yOffset + 6);
          currentX += lineItemCols[4].width;

          // Add total price column for instant orders
          doc.rect(currentX, yOffset, lineItemCols[5].width, itemRowHeight);
          let totalPriceText = item.order_type === 'INSTANT_ORDER' ? 
            (item.price * item.quantity)?.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' }) || '-' : 
            '-';
          const maxWidth = lineItemCols[5].width - 4; // Subtract padding
          
          // If text is too long, try to keep the important parts
          if (doc.getStringUnitWidth(totalPriceText) * doc.internal.getFontSize() > maxWidth) {
            const amount = item.price * item.quantity;
            totalPriceText = amount.toLocaleString('vi-VN') + ' ₫';
          }
          doc.text(totalPriceText, currentX + 2, yOffset + 6);
          currentX += lineItemCols[5].width;

          doc.rect(currentX, yOffset, lineItemCols[6].width, itemRowHeight);
          doc.text(item.note || '-', currentX + 2, yOffset + 6);

          yOffset += itemRowHeight;
        });

        if (yOffset > pageHeight - 20) {
          doc.addPage();
          yOffset = margin;
        }

        // Subtotals by order type

        // Package order total - only count items with convert_rate (truthy)
        const packageTotal = order.order_line_items
        .filter(item => item.quantity > 0)
        .filter(item => item.order_type === 'MONTHLY_PACKAGE_ORDER')
        .filter(item => item.convert_rate)
        .reduce((sum, item) => sum + parseFloat((item.quantity / item.convert_rate).toFixed(2)), 0);

        // Instant order total - use raw quantity (no conversion)
        const instantTotal = order.order_line_items
        .filter(item => item.quantity > 0)
        .filter(item => item.order_type === 'INSTANT_ORDER')
        .reduce((sum, item) => sum + item.quantity, 0);

        currentX = margin;
        doc.rect(currentX, yOffset, lineItemCols[0].width + lineItemCols[1].width, 8);
        doc.text('Tổng số kg (Gói tháng):', currentX + 2, yOffset + 6);
        currentX += lineItemCols[0].width + lineItemCols[1].width;

        doc.rect(currentX, yOffset, lineItemCols[2].width + lineItemCols[3].width + lineItemCols[4].width + lineItemCols[5].width + lineItemCols[6].width, 8);
        doc.text(packageTotal.toFixed(2), currentX + 2, yOffset + 6);

        if (instantTotal > 0) {
          yOffset += 8;
          if (yOffset > pageHeight - 20) {
            doc.addPage();
            yOffset = margin;
          }
          currentX = margin;
          doc.rect(currentX, yOffset, lineItemCols[0].width + lineItemCols[1].width, 8);
          doc.text('Tổng số kg (Mua lẻ):', currentX + 2, yOffset + 6);
          currentX += lineItemCols[0].width + lineItemCols[1].width;

          doc.rect(currentX, yOffset, lineItemCols[2].width, 8);
          doc.text(instantTotal.toFixed(2), currentX + 2, yOffset + 6);

          currentX += lineItemCols[2].width;
          doc.rect(currentX, yOffset, lineItemCols[3].width + lineItemCols[4].width + lineItemCols[5].width + lineItemCols[6].width, 8);
          const instantPrice = order.order_line_items
          .filter(item => item.quantity > 0)
          .filter(item => item.order_type === 'INSTANT_ORDER')
          .reduce((sum, item) => sum + (item.price * item.quantity), 0);
          doc.text(instantPrice.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' }), currentX + 2, yOffset + 6);
        }
        yOffset += 20;
        if (yOffset > pageHeight - 20) {
          doc.addPage();
          yOffset = margin;
        }
      }
    });

    const userName = userPackage.user?.name?.replace(/\s+/g, '_') || 'no_name';
    doc.save(`baocao_${userPackage.id}_${userName}.pdf`);
  };

  const fetchUserPackage = async () => {
    try {
      const userPackageInstance = new UserPackage();
      const success = await userPackageInstance.getById(id);
      if (success) {
        setUserPackage(userPackageInstance);
        setFormData({...userPackageInstance});
        setOriginalData({...userPackageInstance});

        // Use orders from the API response if available
        if (userPackageInstance.orders && userPackageInstance.orders.length > 0) {
          setOrders(userPackageInstance.orders);
          setLoadingOrders(false);
        }

        // Set bonuses from the API response
        if (userPackageInstance.bonuses) {
          setBonuses(userPackageInstance.bonuses);
        }
        if (userPackageInstance.bonus_total !== undefined) {
          setBonusTotal(userPackageInstance.bonus_total);
        }
      }
    } catch (error) {
      console.error('Error fetching user package:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBonusAdded = (newBonus) => {
    setBonuses(prev => [newBonus, ...prev]);
    setBonusTotal(prev => prev + newBonus.amount);
  };

  const handleDeleteBonus = async (bonusId) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa khối lượng tặng thêm này?')) {
      return;
    }

    try {
      const response = await excuseApi({
        path: `/user-packages/${id}/bonuses/${bonusId}`,
        method: 'DELETE',
      });

      if (response.status === 200) {
        const deletedBonus = bonuses.find(b => b.id === bonusId);
        setBonuses(prev => prev.filter(b => b.id !== bonusId));
        setBonusTotal(prev => prev - (deletedBonus?.amount || 0));
      }
    } catch (error) {
      console.error('Error deleting bonus:', error);
    }
  };

  const getBonusReasonLabel = (reason) => {
    const labels = {
      'REFERRAL': 'Giới thiệu khách hàng',
      'PROMOTION': 'Khuyến mãi',
      'COMPENSATION': 'Bồi thường/Đền bù',
      'OTHER': 'Khác',
    };
    return labels[reason] || reason;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const saveChanges = useCallback(async () => {
    try {
      const userPackageInstance = new UserPackage(formData);
      const success = await userPackageInstance.update();
      if (success) {
        setUserPackage(userPackageInstance);
        setFormData({...userPackageInstance});
        setOriginalData({...userPackageInstance});
        setEditMode(false);
      }
    } catch (error) {
      console.error('Error updating user package:', error);
    }
  }, [formData]);

  if (loading) {
    return (
      <Container sx={{ mt: 3 }}>
        <Skeleton variant="rectangular" height={200} />
      </Container>
    );
  }

  if (!userPackage) {
    return (
      <Container sx={{ mt: 3 }}>
        <Typography variant="h6" color="error">
          Không tìm thấy gói dịch vụ
        </Typography>
      </Container>
    );
  }

  // Already settled — nothing left to invoice, and the status is invoice-driven.
  const isPackagePaid = ['PAID', 'PAID_UNBILLED_ITEMS'].includes(userPackage.financial_status);
  // S20: instant items delivered after an earlier invoice still need billing.
  const hasUnbilledItems = userPackage.financial_status === 'PAID_UNBILLED_ITEMS';
  const createInvoiceBlocked = isPackagePaid && !hasUnbilledItems;
  // An OPEN invoice (draft or sent) is where new items belong — both are still
  // editable and syncable (S15/S19), so don't offer a second one alongside it.
  const hasOpenInvoice = packageInvoices.some((inv) => ['DRAFT', 'SENT'].includes(inv.status));

  const DetailRow = ({ label, value }) => (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={3}>
        <Typography variant="subtitle2" color="text.secondary">
          {label}
        </Typography>
      </Grid>
      <Grid item xs={9}>
        <Typography>{value}</Typography>
      </Grid>
    </Grid>
  );

  return (
    <Container sx={{ mt: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/user-packages')}
          sx={{ mr: 2 }}
        >
          Quay lại
        </Button>
        <Button
          variant="outlined"
          onClick={exportToPDF}
          sx={{ mr: 2 }}
        >
          Xuất PDF
        </Button>
        {userPackage.status === 'ACTIVE' && (
          <Button
            variant="outlined"
            color="success"
            onClick={() => setBonusDialogOpen(true)}
            startIcon={<CardGiftcardIcon />}
            sx={{ mr: 2 }}
          >
            Thêm KL tặng
          </Button>
        )}
        {editMode ? (
          <>
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                setFormData({...originalData});
                setEditMode(false);
              }}
              sx={{ mr: 2 }}
            >
              Hủy
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={saveChanges}
            >
              Lưu
            </Button>
          </>
        ) : (
            <Button
              variant="contained"
              onClick={() => setEditMode(true)}
            >
              Chỉnh sửa
            </Button>
          )}
      </Box>

      {/* S21: invoices get their own section — a package can carry several
          (the original plus follow-ups), which crowded the page action bar. */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="h6">Hóa đơn của gói</Typography>
          {hasUnbilledItems && (
            <Chip size="small" color="warning"
              label="Còn mục mua lẻ chưa xuất hóa đơn" />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          {packageInvoices.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
              Chưa có hóa đơn nào.
            </Typography>
          )}
          {packageInvoices.map((inv) => (
            <Button
              key={inv.id}
              variant="outlined"
              color="success"
              size="small"
              onClick={() => navigate(`/invoices/${inv.id}`)}
            >
              {`${inv.code} · ${invoiceStatusTranslations[inv.status] || inv.status}`}
            </Button>
          ))}
          {(packageInvoices.length === 0 || (hasUnbilledItems && !hasOpenInvoice)) && (
            <Tooltip title={createInvoiceBlocked
              ? 'Gói dịch vụ đã được thanh toán và không còn mục mua lẻ chưa xuất hóa đơn'
              : (hasUnbilledItems ? 'Xuất hóa đơn cho các mục mua lẻ phát sinh sau' : '')}>
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={() => navigate('/invoices/new', {
                    state: {
                      customer: { id: userPackage.user_id, name: userPackage.user?.name },
                      preselectPackageIds: [userPackage.id],
                    },
                  })}
                  disabled={createInvoiceBlocked}
                >
                  {hasUnbilledItems ? 'Tạo hóa đơn bổ sung' : 'Tạo hóa đơn & báo cáo'}
                </Button>
              </span>
            </Tooltip>
          )}
          {hasOpenInvoice && hasUnbilledItems && (
            <Typography variant="body2" color="text.secondary">
              Thêm mục mua lẻ mới vào hóa đơn đang mở bằng "Đồng bộ từ gói dịch vụ".
            </Typography>
          )}
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
            {editMode ? (
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <DetailRow
              label="Mã gói dịch vụ"
              value={userPackage.id}
            />

            <DetailRow
              label="Khách hàng"
              value={userPackage.user?.name || '-'}
            />

            <DetailRow
              label="Gói dịch vụ"
              value={userPackage.package?.name || '-'}
            />

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Ngày bắt đầu"
                value={dayjs(formData.beginning_date)}
                onChange={(newValue) => setFormData(prev => ({ ...prev, beginning_date: newValue }))}
                slotProps={{
                  textField: {
                    required: true,
                    fullWidth: true
                  }
                }}
                format="DD/MM/YYYY"
              />
            </LocalizationProvider>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Ngày kết thúc"
                value={formData.ending_date ? dayjs(formData.ending_date) : null}
                onChange={(newValue) => setFormData(prev => ({ ...prev, ending_date: newValue }))}
                slotProps={{
                  textField: {
                    fullWidth: true
                  }
                }}
                format="DD/MM/YYYY"
              />
            </LocalizationProvider>

            <FormControl>
              <InputLabel>Trạng thái *</InputLabel>
              <Select
                value={formData.status}
                label="Trạng thái *"
                required
                onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
              >
                <MenuItem value="ACTIVE">{userPackageStatusTranslations.ACTIVE}</MenuItem>
                <MenuItem value="INACTIVE">{userPackageStatusTranslations.INACTIVE}</MenuItem>
                <MenuItem value="DELETED">{userPackageStatusTranslations.DELETED}</MenuItem>
                <MenuItem value="DONE">{userPackageStatusTranslations.DONE}</MenuItem>
              </Select>
            </FormControl>

            <FormControl>
              <InputLabel>Trạng thái thanh toán *</InputLabel>
              <Select
                value={formData.financial_status}
                label="Trạng thái thanh toán *"
                required
                onChange={(e) => setFormData(prev => ({ ...prev, financial_status: e.target.value }))}
              >
                {/* PAID is set by paying the package's invoice; only an admin may
                    force it by hand (the backend returns 403 for staff). */}
                {(isAdmin || isPackagePaid) && (
                  <MenuItem value="PAID">{financialStatusTranslations.PAID}</MenuItem>
                )}
                {/* S17: normally set automatically when an instant line lands on a
                    package order; selectable so staff can flag older packages too. */}
                <MenuItem value="PAID_UNBILLED_ITEMS">
                  {financialStatusTranslations.PAID_UNBILLED_ITEMS}
                </MenuItem>
                <MenuItem value="UNPAID">{financialStatusTranslations.UNPAID}</MenuItem>
                <MenuItem value="PENDING">{financialStatusTranslations.PENDING}</MenuItem>
              </Select>
              <FormHelperText>
                {isAdmin
                  ? 'Trạng thái này được tự động cập nhật khi hóa đơn được thanh toán.'
                  : 'Được cập nhật tự động từ hóa đơn; chỉ quản trị viên đánh dấu thủ công.'}
              </FormHelperText>
            </FormControl>

            <FormControl>
              <InputLabel>Trạng thái gửi báo cáo *</InputLabel>
              <Select
                value={formData.sending_invoice_status}
                label="Trạng thái gửi báo cáo *"
                required
                onChange={(e) => setFormData(prev => ({ ...prev, sending_invoice_status: e.target.value }))}
              >
                <MenuItem value="SENT">{sendingInvoiceStatusTranslations.SENT}</MenuItem>
                <MenuItem value="PENDING">{sendingInvoiceStatusTranslations.PENDING}</MenuItem>
                <MenuItem value="FAILED">{sendingInvoiceStatusTranslations.FAILED}</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Ghi chú"
              multiline
              rows={4}
              value={formData.note || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, note: e.target.value }))}
            />
          </Box>
        ) : (
            <>
              <DetailRow
                label="Mã gói dịch vụ"
                value={userPackage.id}
              />

              <DetailRow
                label="Khách hàng"
                value={userPackage.user?.name || '-'}
              />

              <DetailRow
                label="Địa chỉ khách hàng"
                value={userPackage.user?.address || '-'}
              />

              <DetailRow
                label="Gói dịch vụ"
                value={userPackage.package?.name || '-'}
              />

              <DetailRow
                label="Ngày bắt đầu"
                value={dayjs(userPackage.beginning_date).format('DD/MM/YYYY')}
              />

              <DetailRow
                label="Ngày kết thúc"
                value={userPackage.ending_date
                  ? dayjs(userPackage.ending_date).format('DD/MM/YYYY')
                  : '-'}
              />
              
              <DetailRow
                label="Đơn hàng cuối"
                value={(() => {
                  // Find the most recent processed_at date from all non-cancelled orders
                  const maxProcessedAt = orders
                    .filter(order => !['CANCELLED', 'CONFIRMING'].includes(order.status) && order.processed_at)
                    .reduce((maxDate, order) => {
                      const orderDate = dayjs(order.processed_at);
                      return !maxDate || orderDate.isAfter(maxDate) ? orderDate : maxDate;
                    }, null);
                  
                  return maxProcessedAt ? maxProcessedAt.format('DD/MM/YYYY') : '-';
                })()}
              />

              <DetailRow
                label="Số lượng đơn hàng"
                value={orders.filter(order => !['CANCELLED', 'CONFIRMING'].includes(order.status)).length}
              />

              <DetailRow
                label="Số kg mua gói tháng"
                value={orders
                  .filter(order => !['CANCELLED', 'CONFIRMING'].includes(order.status))
                  .reduce((total, order) =>
                    total + order.order_line_items
                      .filter(item => item.order_type === 'MONTHLY_PACKAGE_ORDER')
                      .filter(item => item.convert_rate)
                      .reduce((sum, item) =>
                        sum + parseFloat((item.quantity / item.convert_rate).toFixed(2)), 0
                      ), 0
                  ).toFixed(2)
                }
              />

              <DetailRow
                label="Số kg mua lẻ"
                value={orders
                  .filter(order => !['CANCELLED', 'CONFIRMING'].includes(order.status))
                  .reduce((total, order) =>
                    total + order.order_line_items
                      .filter(item => item.order_type === 'INSTANT_ORDER')
                      .reduce((sum, item) =>
                        sum + item.quantity, 0
                      ), 0
                  ).toFixed(2)
                }
              />

              <DetailRow
                label="Tổng tiền mua lẻ"
                value={orders
                  .filter(order => !['CANCELLED', 'CONFIRMING'].includes(order.status))
                  .reduce((total, order) =>
                    total + order.order_line_items
                      .filter(item => item.order_type === 'INSTANT_ORDER')
                      .reduce((sum, item) =>
                        sum + (item.quantity * (item.price || 0)), 0
                      ), 0
                  ).toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })
                }
              />

              <DetailRow
                label="Tổng số kg"
                value={orders
                  .filter(order => !['CANCELLED', 'CONFIRMING'].includes(order.status))
                  .reduce((total, order) =>
                    total + order.order_line_items
                      .filter(item => item.convert_rate)
                      .reduce((sum, item) =>
                        sum + parseFloat((item.quantity / item.convert_rate).toFixed(2)), 0
                      ), 0
                  ).toFixed(2)
                }
              />

              <DetailRow
                label="Trạng thái"
                value={
                  <Chip
                    label={userPackageStatusTranslations[userPackage.status]}
                    color={getUserPackageStatusColor(userPackage.status)}
                    size="small"
                  />
                }
              />

              <DetailRow
                label="Trạng thái thanh toán"
                value={
                  <Chip
                    label={financialStatusTranslations[userPackage.financial_status] || userPackage.financial_status}
                    color={getFinancialStatusColor(userPackage.financial_status)}
                    size="small"
                  />
                }
              />

              <DetailRow
                label="Ghi chú"
                value={userPackage.note || '-'}
              />

              <DetailRow
                label="Trạng thái gửi báo cáo"
                value={
                  <Chip
                    label={sendingInvoiceStatusTranslations[userPackage.sending_invoice_status] || userPackage.sending_invoice_status}
                    color={getSendingInvoiceStatusColor(userPackage.sending_invoice_status)}
                    size="small"
                  />
                }
              />

              {
                userPackage.sent_invoice_at && (
                  <DetailRow
                    label="Thời gian gửi báo cáo"
                    value={dayjs(userPackage.sent_invoice_at).format('DD/MM/YYYY HH:mm')}
                  />
                )
              }

              {
                userPackage.sent_invoice_by && (
                  <DetailRow
                    label="Người gửi báo cáo"
                    value={userPackage.sent_invoice_by.name}
                  />
                )
              }

              {
                userPackage.created_by && (
                  <DetailRow
                    label="Người tạo"
                    value={userPackage.created_by.name}
                  />
                )
              }

              {
                userPackage.updated_by && (
                  <DetailRow
                    label="Người cập nhật"
                    value={userPackage.updated_by.name}
                  />
                )
              }
            </>
          )}
      </Paper >

      {/* Bonus Section */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Khối lượng tặng thêm
          </Typography>
          {bonusTotal > 0 && (
            <Chip
              icon={<CardGiftcardIcon />}
              label={`Tổng: ${bonusTotal.toFixed(2)} kg`}
              color="success"
              variant="outlined"
            />
          )}
        </Box>

        {/* Package Summary with Bonus */}
        {userPackage.package && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="text.secondary">Khối lượng gói</Typography>
                <Typography variant="h6">{userPackage.package.amount} kg</Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="text.secondary">Tặng thêm</Typography>
                <Typography variant="h6" color="success.main">+{bonusTotal.toFixed(2)} kg</Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="text.secondary">Tổng khả dụng</Typography>
                <Typography variant="h6">{(userPackage.package.amount + bonusTotal).toFixed(2)} kg</Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="text.secondary">Còn lại</Typography>
                {(() => {
                  const usedMonthly = orders
                    .filter(order => !['CANCELLED', 'CONFIRMING'].includes(order.status))
                    .reduce((total, order) =>
                      total + order.order_line_items
                        .filter(item => item.order_type === 'MONTHLY_PACKAGE_ORDER')
                        .filter(item => item.convert_rate)
                        .reduce((sum, item) =>
                          sum + parseFloat((item.quantity / item.convert_rate).toFixed(2)), 0
                        ), 0
                    );
                  const remaining = userPackage.package.amount + bonusTotal - usedMonthly;
                  const color = remaining < 2 ? 'error.main' : remaining < 5 ? 'warning.main' : 'success.main';
                  return (
                    <Typography variant="h6" color={color}>{remaining.toFixed(2)} kg</Typography>
                  );
                })()}
              </Grid>
            </Grid>
          </Box>
        )}

        {bonuses.length > 0 ? (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Ngày thêm</TableCell>
                  <TableCell align="right">Số lượng (kg)</TableCell>
                  <TableCell>Lý do</TableCell>
                  <TableCell>Ghi chú</TableCell>
                  <TableCell>Người thêm</TableCell>
                  <TableCell align="center">Thao tác</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bonuses.map((bonus) => (
                  <TableRow key={bonus.id}>
                    <TableCell>{dayjs(bonus.created_at).format('DD/MM/YYYY HH:mm')}</TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`+${bonus.amount.toFixed(2)}`}
                        color="success"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{getBonusReasonLabel(bonus.reason)}</TableCell>
                    <TableCell>{bonus.note || '-'}</TableCell>
                    <TableCell>{bonus.created_by?.name || '-'}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Xóa">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteBonus(bonus.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography color="text.secondary">
            Chưa có khối lượng tặng thêm nào
          </Typography>
        )}
      </Paper>

      <Paper sx={{ p: 3, mt: 3 }}>
        {loadingOrders ? (
          <Skeleton variant="rectangular" height={200} />
        ) : orders.length > 0 ? (
            <>
              {/* Original Orders Table */}
              < Typography variant="h6" gutterBottom >
                Danh sách đơn hàng
              </Typography >
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Mã đơn hàng</TableCell>
                      <TableCell>Tên đơn hàng</TableCell>
                      <TableCell>Loại đơn hàng</TableCell>
                      <TableCell>Trạng thái</TableCell>
                      <TableCell>Ngày xử lý</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orders.filter(order => !['CANCELLED', 'CONFIRMING'].includes(order.status)).map((order) => (
                      <React.Fragment key={order.id}>
                        <TableRow
                          sx={{
                            '&:last-child td, &:last-child th': { border: 0 },
                            '& > *': { borderBottom: 'unset' }
                          }}
                        >
                          <TableCell>{order.id}</TableCell>
                          <TableCell>{order.name}</TableCell>
                          <TableCell>{order.order_type === 'INSTANT_ORDER' ? 'Mua lẻ' : order.order_type === 'MONTHLY_PACKAGE_ORDER' ? 'Gói tháng' : order.order_type || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              label={order.status === 'OPEN' ? 'Mới' :
                                order.status === 'CANCELLED' ? 'Đã hủy' :
                                  order.status === 'SENT_INVOICE' ? 'Đã gửi hóa đơn' :
                                    order.status}
                              size="small"
                              color={order.status === 'OPEN' ? 'warning' :
                                order.status === 'CANCELLED' ? 'default' :
                                  order.status === 'SENT_INVOICE' ? 'success' :
                                    'primary'}
                            />
                          </TableCell>
                          <TableCell>
                            {order.processed_at ? dayjs(order.processed_at).format('DD/MM/YYYY') : '-'}
                          </TableCell>
                        </TableRow >
                        {
                          order.order_line_items && order.order_line_items.filter(item => item.quantity > 0).length > 0 && (
                            <TableRow>
                              <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                                <Box sx={{ margin: 1 }}>
                                  <Typography variant="subtitle2" gutterBottom component="div">
                                    Chi tiết đơn hàng:
                                  </Typography>
                                  <Table size="small" aria-label="line items">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Sản phẩm</TableCell>
                                        <TableCell align="right">Số lượng (Thực nhận)</TableCell>
                                        <TableCell align="right">Số lượng (Quy đổi gói tháng)</TableCell>
                                        <TableCell align="right">Đơn giá</TableCell>
                                        <TableCell align="right">Thành tiền</TableCell>
                                        <TableCell>Loại</TableCell>
                                        <TableCell>Ghi chú</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {order.order_line_items.filter(item => item.quantity > 0).map((item, index) => (
                                        <TableRow key={index}>
                                          <TableCell component="th" scope="row">
                                            {item.product?.name || 'N/A'}
                                          </TableCell>
                                          <TableCell align="right">{item.quantity}</TableCell>
                                          <TableCell align="right">{item.convert_rate ? (item.quantity / item.convert_rate).toFixed(2) : '-'}</TableCell>
                                          <TableCell align="right">
                                            {item.order_type === 'INSTANT_ORDER' ? item.price?.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' }) || '-' : '-'}
                                          </TableCell>
                                          <TableCell align="right">
                                            {item.order_type === 'INSTANT_ORDER' ? (item.price * item.quantity)?.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' }) || '-' : '-'}
                                          </TableCell>
                                          <TableCell>{item.order_type === 'INSTANT_ORDER' ? 'Mua lẻ' : item.order_type === 'MONTHLY_PACKAGE_ORDER' ? 'Gói tháng' : item.order_type || '-'}</TableCell>
                                          <TableCell>{item.note || '-'}</TableCell>
                                        </TableRow >
                                      ))}
                                      <TableRow>
                                        {['MONTHLY_PACKAGE_ORDER', 'INSTANT_ORDER'].map(type => {
                                          const typeTotal = type === 'MONTHLY_PACKAGE_ORDER'
                                            ? order.order_line_items
                                                .filter(item => item.order_type === type)
                                                .filter(item => item.convert_rate)
                                                .reduce((sum, item) => sum + parseFloat((item.quantity / item.convert_rate).toFixed(2)), 0)
                                            : order.order_line_items
                                                .filter(item => item.order_type === type)
                                                .reduce((sum, item) => sum + item.quantity, 0);

                                          if (typeTotal > 0) {
                                            return (
                                              <TableRow key={type}>
                                                <TableCell colSpan={2} align="right">
                                                  <strong>Tổng số kg ({type === 'MONTHLY_PACKAGE_ORDER' ? 'Gói tháng' : 'Mua lẻ'}):</strong>
                                                </TableCell>
                                                <TableCell align="right">
                                                  <strong>{typeTotal.toFixed(2)}</strong>
                                                </TableCell>
                                                <TableCell />
                                                <TableCell align="right">
                                                  <strong>
                                                    {type === 'INSTANT_ORDER' ? 
                                                      order.order_line_items
                                                      .filter(item => item.order_type === type)
                                                      .reduce((sum, item) => sum + (item.price * item.quantity), 0)
                                                      .toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })
                                                      : '-'}
                                                  </strong>
                                                </TableCell>
                                                <TableCell />
                                                <TableCell />
                                              </TableRow>
                                            );
                                          }
                                          return null;
                                        })}
                                        <TableRow>
                                          <TableCell colSpan={2} align="right">
                                            <strong>Tổng cộng:</strong>
                                          </TableCell>
                                          <TableCell align="right">
                                            <strong>
                                              {order.order_line_items
                                                .filter(item => item.convert_rate)
                                                .reduce((sum, item) =>
                                                  sum + parseFloat((item.quantity / item.convert_rate).toFixed(2)), 0
                                                ).toFixed(2)}
                                            </strong>
                                          </TableCell>
                                          <TableCell />
                                          <TableCell align="right">
                                            <strong>
                                              {order.order_line_items
                                                .filter(item => item.order_type === 'INSTANT_ORDER')
                                                .reduce((sum, item) => sum + (item.price * item.quantity), 0)
                                                .toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })}
                                            </strong>
                                          </TableCell>
                                          <TableCell />
                                          <TableCell />
                                        </TableRow>
                                      </TableRow>
                                    </TableBody >
                                  </Table >
                                </Box >
                              </TableCell >
                            </TableRow >
                          )
                        }
                      </React.Fragment >
                    ))}
                  </TableBody >
                </Table >
              </TableContainer >
            </>
          ) : (
              <Typography color="text.secondary">
                Chưa có đơn hàng nào
              </Typography>
            )
        }
      </Paper >

      {/* Related Menus */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            <MenuBookIcon sx={{ mr: 1, verticalAlign: 'middle' }} fontSize="small" />
            Menu liên quan
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => navigate('/menus/new')}
          >
            Tạo menu mới
          </Button>
        </Box>
        {loadingMenus ? (
          <Skeleton variant="rectangular" height={100} />
        ) : menus.length > 0 ? (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Tiêu đề</TableCell>
                  <TableCell>Khách hàng</TableCell>
                  <TableCell>Khối lượng</TableCell>
                  <TableCell>Số lần giao</TableCell>
                  <TableCell>Trạng thái</TableCell>
                  <TableCell>Ngày tạo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {menus.map((menu) => (
                  <TableRow
                    key={menu.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/menus/${menu.id}/edit`)}
                  >
                    <TableCell>{menu.id}</TableCell>
                    <TableCell>{menu.title}</TableCell>
                    <TableCell>{menu.customer_name}</TableCell>
                    <TableCell>{menu.total_weight}kg</TableCell>
                    <TableCell>{menu.total_deliveries}</TableCell>
                    <TableCell>
                      <Chip
                        label={menu.status === 'DRAFT' ? 'Nháp' : menu.status === 'ACTIVE' ? 'Đang dùng' : menu.status}
                        size="small"
                        color={menu.status === 'ACTIVE' ? 'success' : menu.status === 'DRAFT' ? 'default' : 'warning'}
                      />
                    </TableCell>
                    <TableCell>
                      {menu.created_at ? dayjs(menu.created_at).format('DD/MM/YYYY') : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography color="text.secondary">
            Chưa có menu nào
          </Typography>
        )}
      </Paper>

      {/* Add Bonus Dialog */}
      <AddBonusDialog
        open={bonusDialogOpen}
        onClose={() => setBonusDialogOpen(false)}
        userPackageId={id}
        onSuccess={handleBonusAdded}
      />
    </Container >
  );
};

export default UserPackageDetail;
